/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { AsyncApiCallHandler } from 'src/core/types/AsyncApiCallHandler';
import { LuisRequestOptions } from 'src/core/types/LuisRequestOptions';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';

type UrlBuilder = (trial?: number) => string;

/**
 * Service calls certain `url` with `apiOptions` many times with exponential back off
 * until certain condition meet
 * the service takes `serviceHandler` to handle the response of each call
 * the service calls the API with GET method
 *
 * @param url The url of the API to hit or a function that creates that url.
 * @param apiOptions The API options
 * @param serviceHandler The handler function that handles the response of the API call
 * @template T The formatted response data (after calling mapApiData on it)
 * @template Y The data received from the backend.
 */
export const createAsyncApiCall = async <T, Y>(
    url: string | UrlBuilder,
    apiOptions: LuisRequestOptions,
    serviceHandler: AsyncApiCallHandler<T, Y>,
    trial: number = 0,
    delayFunction: (_: object, trial?: number) => number = OperationDelayStrategies.exponentialDelayStrategy
): Promise<T | Y> => {
    try {
        const urlToCall = typeof url === 'string' ? url : url(trial);

        const response = await httpService.get<Y>(urlToCall, apiOptions);
        const { data } = response;
        const models = serviceHandler.mapApiData ? serviceHandler.mapApiData(data) : data;

        if (serviceHandler.handleCall !== undefined) {
            serviceHandler.handleCall(<T>models, response);
        }

        if (serviceHandler.shouldRetry(response)) {
            await new Promise(resolve => setTimeout(resolve, delayFunction(null, trial)));

            return await createAsyncApiCall(url, apiOptions, serviceHandler, ++trial, delayFunction);
        }

        return models;
    } catch (error) {
        serviceHandler.onError(<Error>error);
    }
};
